/**
 * Carrying a plan choice across the Google sign-in boundary.
 *
 * Every pricing CTA used to call the same bare `onLogin`, so whichever card a
 * lawyer clicked was discarded at the popup. The cards even announced the plan
 * to screen readers (`aria-label="Start your 30-day trial — Criminal Max"`)
 * and then threw it away — the button promised something the system did not do.
 *
 * sessionStorage rather than a URL param: the Google popup returns to the same
 * document, there is no redirect to hang a query string on, and the intent must
 * not outlive the tab (a plan clicked last week should not hijack a later
 * sign-in).
 */
import type { Tier, Vertical } from './planCardState';
import { ROLE_LABELS } from '../../../types/practiceArea';

const KEY = 'nyayabodh_plan_intent';

export interface PlanIntent {
  vertical: Vertical;
  tier: Tier;
}

/**
 * Landing-band practice label -> billing vertical.
 *
 * KEYED OFF ROLE_LABELS, NOT LITERALS. PricingBand looks a card's displayed
 * practice name up in this map to decide which vertical to sell, so a literal
 * key here is a rename away from a silent miss — wrong vertical at checkout,
 * no error. Computed keys make the display string and the billing key the same
 * value by construction. It had already drifted once: the map said
 * 'Civil & Consumer' while the app said 'Consumer & Civil'.
 */
export const VERTICAL_BY_PRACTICE: Record<string, Vertical> = {
  [ROLE_LABELS.criminal]: 'criminal',
  [ROLE_LABELS.civil]: 'civil',
  [ROLE_LABELS.corporate]: 'corporate',
};

/** Card tier label ("Pro" | "Max" | "Firm" | "Firm Max") -> billing tier. */
export function tierFromPlanName(name: string): Tier {
  return name.toLowerCase().includes('max') ? 'max' : 'pro';
}

export function rememberPlanIntent(intent: PlanIntent): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(intent));
  } catch {
    // Private mode / storage disabled: the sign-in must still work. Losing the
    // intent costs one extra click on the in-app plan picker; blocking sign-in
    // costs the customer.
  }
}

/** Read and CLEAR — an intent is consumed once, never replayed. */
export function takePlanIntent(): PlanIntent | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    sessionStorage.removeItem(KEY);
    const parsed = JSON.parse(raw);
    if (!parsed?.vertical || !parsed?.tier) return null;
    return parsed as PlanIntent;
  } catch {
    return null;
  }
}
